import * as React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import type {
  AgreementDetails,
  RegistrationAgreementClause,
} from "../../domain/entities/Agreement";
import AuthPrimaryButton from "./AuthPrimaryButton";
import AuthTextField from "./AuthTextField";
import AgreementMarkdown from "./AgreementMarkdown";
import { shouldShowAgreementTitle } from "../utils/normalizeAgreementContent";
import logo from "@/assets/nimixa-icons/nimixa-logo-stacked.png";

interface AgreementProps {
  open: boolean;
  onClose: () => void;
  onAgree: () => void | Promise<void>;
  agreementDetails: AgreementDetails[];
  acceptanceClauses: RegistrationAgreementClause[];
  expectedName: string;
  loading?: boolean;
  error?: string | null;
}

const SCROLL_END_THRESHOLD_PX = 32;

const scrollSx = {
  overflowY: "auto",
  minHeight: 0,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: "grey.300",
    borderRadius: 3,
  },
} as const;

const textButtonSx = {
  alignItems: "center",
  display: "inline-flex",
  fontFamily: "var(--type-button-font-family)",
  fontSize: "var(--type-button-font-size)",
  fontWeight: "var(--type-button-font-weight)",
  justifyContent: "center",
  lineHeight: "var(--type-button-line-height)",
  textDecoration: "none",
  verticalAlign: "middle",
} as const;

export default function Agreement({
  open,
  onClose,
  onAgree,
  agreementDetails,
  acceptanceClauses,
  expectedName,
  loading = false,
  error = null,
}: AgreementProps) {
  const { t } = useTranslation("auth");
  const STEPS = [t("agreement.stepReview"), t("agreement.stepAccept")];
  const [activeStep, setActiveStep] = React.useState(0);
  const [hasReadToEnd, setHasReadToEnd] = React.useState(false);
  const [acceptedClauses, setAcceptedClauses] = React.useState<
    Record<string, boolean>
  >({});
  const [name, setName] = React.useState("");
  const termsScrollRef = React.useRef<HTMLDivElement>(null);
  const expectedSignatureName = expectedName.trim().toUpperCase();

  const checkScrollPosition = React.useCallback(() => {
    const el = termsScrollRef.current;
    if (!el) return;

    const atBottom =
      el.scrollHeight - el.scrollTop <=
      el.clientHeight + SCROLL_END_THRESHOLD_PX;

    if (atBottom) {
      setHasReadToEnd(true);
    }
  }, []);

  const checkIfScrollNotNeeded = React.useCallback(() => {
    const el = termsScrollRef.current;
    if (!el) return;

    if (el.scrollHeight <= el.clientHeight + SCROLL_END_THRESHOLD_PX) {
      setHasReadToEnd(true);
    }
  }, []);

  React.useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setHasReadToEnd(false);
      setAcceptedClauses({});
      setName("");
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || activeStep !== 0) return;

    const frame = requestAnimationFrame(() => {
      checkIfScrollNotNeeded();
      checkScrollPosition();
    });

    return () => cancelAnimationFrame(frame);
  }, [
    open,
    activeStep,
    agreementDetails,
    checkIfScrollNotNeeded,
    checkScrollPosition,
  ]);

  const allChecked =
    acceptanceClauses.length > 0 &&
    acceptanceClauses.every((clause) => acceptedClauses[clause.id]);
  const normalizedName = name.trim().replace(/\s+/g, " ");
  const normalizedExpectedName = expectedSignatureName.replace(/\s+/g, " ");
  const nameValid =
    normalizedExpectedName.length > 0 &&
    normalizedName === normalizedExpectedName;
  const canRegister = allChecked && nameValid && !loading;
  const canContinue = hasReadToEnd && agreementDetails.length > 0;

  const handleContinue = () => {
    if (canContinue) {
      setActiveStep(1);
    }
  };

  const handleBackToReview = () => {
    setActiveStep(0);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading || reason === "backdropClick") return;
        onClose();
      }}
      fullWidth
      maxWidth="md"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          height: { xs: "95vh", sm: "90vh" },
          maxHeight: { xs: "95vh", sm: "90vh" },
          borderRadius: "20px",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.28)",
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          pt: 2.5,
          pb: 2,
          textAlign: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "grey.100",
              boxShadow: 1,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Nimixa"
              sx={{
                height: 56,
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>

        <Typography variant="h6" fontWeight={600}>
          {t("agreement.title")}
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ mt: 2, mb: 0.5 }}
        >
          {STEPS.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    "&.Mui-completed": { color: "primary.main" },
                    "&.Mui-active": { color: "primary.main" },
                  },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={activeStep === index ? 600 : 400}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1 }}
        >
          {activeStep === 0
            ? t("agreement.hintReview")
            : t("agreement.hintAccept")}
        </Typography>
      </Box>

      {/* Step content */}
      <Box
        sx={{
          flex: "1 1 0",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {activeStep === 0 ? (
          <>
            <Box
              ref={termsScrollRef}
              onScroll={checkScrollPosition}
              sx={{
                ...scrollSx,
                flex: "1 1 0",
                mx: 2,
                mt: 1.5,
                mb: 0,
                px: 2.5,
                py: 2,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: "grey.200",
                bgcolor: "grey.50",
              }}
            >
              {agreementDetails.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t("agreement.loadingTerms")}
                </Typography>
              ) : (
                agreementDetails.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      mb: index < agreementDetails.length - 1 ? 3 : 0,
                      pb: index < agreementDetails.length - 1 ? 3 : 0,
                      borderBottom:
                        index < agreementDetails.length - 1
                          ? "1px solid"
                          : "none",
                      borderColor: "grey.200",
                    }}
                  >
                    {shouldShowAgreementTitle(item.title, item.content) && (
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 1.5 }}
                      >
                        {item.title}
                      </Typography>
                    )}
                    <AgreementMarkdown content={item.content} />
                  </Box>
                ))
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: 2.5,
                py: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              {!hasReadToEnd && agreementDetails.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  sx={{ mb: 1.5 }}
                >
                  {t("agreement.scrollToContinue")}
                </Typography>
              )}

              <AuthPrimaryButton
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
              >
                {t("agreement.continueToAcceptance")}
              </AuthPrimaryButton>

              <Box
                component="button"
                type="button"
                onClick={onClose}
                sx={{
                  display: "block",
                  width: "100%",
                  mt: 1,
                  border: 0,
                  bgcolor: "transparent",
                  cursor: "pointer",
                  color: "primary.main",
                  ...textButtonSx,
                  py: 0.5,
                }}
              >
                {t("agreement.backToRegistrationForm")}
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              px: 2.5,
              py: 2,
            }}
          >
            <Box sx={{ ...scrollSx, flex: "1 1 0", mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("agreement.confirmAcceptance")}
              </Typography>

              <FormGroup sx={{ gap: 1 }}>
                {acceptanceClauses.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {t("agreement.loadingStatements")}
                  </Typography>
                ) : (
                  acceptanceClauses.map(({ id, label }) => (
                    <FormControlLabel
                      key={id}
                      sx={{ alignItems: "flex-start", mx: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={acceptedClauses[id] ?? false}
                          onChange={() =>
                            setAcceptedClauses((prev) => ({
                              ...prev,
                              [id]: !prev[id],
                            }))
                          }
                          disabled={loading}
                          sx={{ py: 0, mt: 0.25 }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          lineHeight={1.5}
                        >
                          {label}
                        </Typography>
                      }
                    />
                  ))
                )}
              </FormGroup>
            </Box>

            <Box sx={{ flexShrink: 0 }}>
              <AuthTextField
                fullWidth
                size="small"
                label={t("agreement.nameLabel")}
                placeholder={t("agreement.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                error={name.length > 0 && !nameValid}
                helperText={
                  name.length > 0 && !nameValid
                    ? t("agreement.nameMismatch", { name: expectedSignatureName })
                    : !allChecked
                      ? t("agreement.acceptAllStatements", { count: acceptanceClauses.length })
                      : undefined
                }
                sx={{ mb: 1.5 }}
              />

              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
                  {error}
                </Typography>
              )}

              <AuthPrimaryButton
                type="button"
                onClick={() => void onAgree()}
                disabled={!canRegister}
                loading={loading}
                sx={{ mb: 1 }}
              >
                {t("agreement.register")}
              </AuthPrimaryButton>

              <Box
                component="button"
                type="button"
                onClick={handleBackToReview}
                disabled={loading}
                sx={{
                  display: "block",
                  width: "100%",
                  border: 0,
                  bgcolor: "transparent",
                  cursor: loading ? "not-allowed" : "pointer",
                  color: "primary.main",
                  ...textButtonSx,
                  py: 0.5,
                  mb: 0.5,
                  opacity: loading ? 0.5 : 1,
                }}
              >
                {t("agreement.backToReview")}
              </Box>

              <Box
                component="button"
                type="button"
                onClick={onClose}
                disabled={loading}
                sx={{
                  display: "block",
                  width: "100%",
                  border: 0,
                  bgcolor: "transparent",
                  cursor: loading ? "not-allowed" : "pointer",
                  color: "text.secondary",
                  ...textButtonSx,
                  py: 0.5,
                  opacity: loading ? 0.5 : 1,
                }}
              >
                {t("agreement.backToRegistrationForm")}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
